import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { useParams } from 'react-router-dom'
import moment from 'moment'

import CongestionChart from './CongestionChart'
import MasterData from '../../data/MasterData'

const StationViewDetail = (props) => {
  const {
    station,
    locations,
    currentCongestions,
    congestionTrends,
    themeName,
    fetchStationDetail,
    fetchLocationData,
    fetchCurrentCongestionRate,
    fetchCongestionTrend
  } = props

  const intl = useIntl()
  const locale = intl.locale
  const { stationId } = useParams()

  useEffect(() => {
    window.scrollTo(0, 0)

    // 駅詳細から遷移してきた時は再取得しない
    const fetchData = async () => {
      if (!station) {
        await fetchStationDetail(stationId)
      }

      for (const locationId of locationIds())  {
        fetchLocationData(stationId, locationId)
        fetchCurrentCongestionRate(stationId, locationId)
        fetchCongestionTrend(stationId, locationId)
      }
    }

    fetchData()
  }, [])

  const isOutOfOrder = () => {
    const now = moment.tz('Asia/Tokyo')
    const minutesFromMidnight = now.hour() * 60 + now.minute()

    // 1:00 - 4:30 までの判定
    return (60 <= minutesFromMidnight && minutesFromMidnight <= 270)
  }

  // 停止すべきかどうか
  // 1箇所でも30分以上更新がないとき
  const shouldSuspend = () => {
    if (!locations) return false

    const now = moment.tz('Asia/Tokyo')

    return locationIds().reduce((prevResult, locationId) => {
      const location = locations[locationId]
      if (!location) return false

      const updated = moment.unix(location.updated).tz('Asia/Tokyo')
      const isOver30Min = 30 * 60 < ((now - updated) / 1000)  // 30 * 60 = 1800秒以上更新がないか
      return prevResult || isOver30Min
    }, false)
  }

  const isConstruction = () => {

    moment.tz.setDefault('Asia/Tokyo')
    // 南万騎が原駅(21)：2020/5/15（金）終電後～(実際には18時)
    // 海老名駅(19)　　：2020/6/10（水）終電後～(実際には18時)
    // 星川駅( 5)　　　：2020/6/17（水）終電後～(実際には18時)
    // 天王町駅( 4)　　：2020/12/29（火）01:30〜（28日営業日の終電後）
    // 2021/9/1 始発後に全て解除

    return (
      (
        (parseInt(stationId, 10) === 21 && moment().isAfter(moment('2020-05-15 18:00:00'))) ||
        (parseInt(stationId, 10) === 19 && moment().isAfter(moment('2020-06-10 18:00:00'))) ||
        (parseInt(stationId, 10) ===  5 && moment().isAfter(moment('2020-06-17 18:00:00'))) ||
        (parseInt(stationId, 10) ===  4 && moment().isAfter(moment('2020-12-29 01:30:00')))
      ) && moment().isBefore(moment('2021-09-01 04:30:00'))
    )
  }

  const locationIds = () => {
    if (station) {
      return station.locationIds
    } else {
      return []
    }
  }

  const renderHeader = () => {
    if (!station) return null

    return(
      <div>
        <header className={`d-StationView-header is_${themeName}`}>{station[`name_${locale}`]}</header>
        <ul className="d-StationView-iconList">
          <li className="d-StationView-iconItem">
            <span className="d-StationView-iconItemImg is_move"></span>
            動いている人
          </li>
          <li className="d-StationView-iconItem">
            <span className="d-StationView-iconItemImg is_stop"></span>
            止まっている人
          </li>
        </ul>
      </div>
    )
  }

  const renderFooter = () => {
    return (
      <div className="d-StationView-note">
        <p className="d-StationView-noteTitle">Station Viewについて</p>
        <p className="d-StationView-noteText">
          本アプリはStation View機能（駅混雑状況の画像配信）を有します。混雑状況の画像データは、個人情報保護の観点から、駅構内に設置しているアプリ専用カメラの画像よりデータ加工技術を用いて解析し、無人の改札口画像（背景画像）に人型アイコンを配置後、匿名情報として相鉄線アプリに配信しています。なお、画像等の保持は一切行いません。
          <br />
          本機能のサービス提供時間は 04:30 〜 24:59 です。
        </p>
      </div>
    )
  }

  const renderLocation = (locationId) => {
    const location = locations[locationId]

    if (location) {
      return (
        <div className="d-StationView-contentItem">
          <p className="d-StationView-contentTitle">{location.title}</p>
          <div className="d-StationView-contentWindow">
            <img
              className="d-StationView-contentImage"
              src={`data:image/png;base64,${location.image}`}
            />
          </div>
        </div>
      )
    }
  }

  const renderChart = (locationId) => {
    const currentCongestion = currentCongestions[locationId]
    const congestionTrend = congestionTrends[locationId]

    if (currentCongestion && congestionTrend) {
      return <CongestionChart
        currentCongestionRate={currentCongestion}
        congestionTrend={congestionTrend}
      />
    }
  }

  const renderOutOfOrder = () => {
    return (
      <div>
        {renderHeader()}
        <div className="d-StationView-outOfOrder">
          <span>01:00〜04:30 は画像配信いたしません。</span>
        </div>
      </div>
    )
  }

  const renderSuspension = () => {
    return (
      <div>
        {renderHeader()}
        <div className="d-StationView-outOfOrder">
          <span>大変申し訳ございません。<br />現在、当駅では使用できません。</span>
        </div>
      </div>
    )
  }

  const renderConstruction = () => {
    return (
      <div>
        {renderHeader()}
        <div className="d-StationView-outOfOrder">
          <span>
            当駅は駅舎改修工事の関係で、
            <br />
            Station Viewを当面の間ご利用になれません。
            <br /><br />
            お客さまには大変ご迷惑をおかけいたしますが
            <br />
            ご理解の程宜しくお願いします。
          </span>
        </div>
      </div>
    )
  }

  if (!station) return null
  if (isConstruction()) return renderConstruction()
  if (isOutOfOrder()) return renderOutOfOrder()
  if (shouldSuspend()) return renderSuspension()

  return (
    <div>
      {renderHeader()}

      {locationIds().map(locationId => {
        return (
          <div key={`stationViewContent${locationId}`} className="d-StationView-content">
            {renderLocation(locationId)}
            {(() => {
              if (station.id !== 28) // 羽沢駅は暫定的に非表示とする
                return (
                  <div className="d-StationView-contentItem">
                    <p className="d-StationView-contentTitle">混雑度グラフ</p>
                    <div className="d-StationView-graph">
                      {renderChart(locationId)}
                    </div>
                    <p className="d-StationView-graphText">棒グラフ：曜日ごとの混雑傾向　　赤折れ線：本日の混雑実績</p>
                  </div>
                )
            })()}
          </div>
        )
      })}

      {renderFooter()}
    </div>
  )
}

StationViewDetail.propTypes = {
  station: PropTypes.object,
  locations: PropTypes.object,
  currentCongestions: PropTypes.object,
  congestionTrends: PropTypes.object,
  themeName: PropTypes.string.isRequired,
  intl: PropTypes.object.isRequired,
  fetchStationDetail: PropTypes.func.isRequired,
  fetchLocationData: PropTypes.func.isRequired,
  fetchCurrentCongestionRate: PropTypes.func.isRequired,
  fetchCongestionTrend: PropTypes.func.isRequired,
}

export default StationViewDetail
